// frontend/src/components/wallet/FundWalletModal.js

import React, { useState } from 'react';
import { X, CreditCard, AlertCircle } from 'lucide-react';
import useWalletStore from '../../../store/walletStore';

const FundWalletModal = ({ onClose, onSuccess }) => {
  const [amount, setAmount] = useState('');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  const { fundWallet, isLoading } = useWalletStore();

  const presetAmounts = [500, 1000, 2000, 5000, 10000, 20000];
  const MINIMUM_FUNDING = 100;
  const MAXIMUM_FUNDING = 1000000;
  const FEE_PERCENT = 2;
  const FEE_CAP = 50;

  const calculateFee = (gross) => {
    if (!gross || gross < MINIMUM_FUNDING) return 0;
    const fee = (gross * FEE_PERCENT) / 100;
    return Math.min(fee, FEE_CAP);
  };

  const handlePresetSelect = (presetAmount) => {
    setAmount(presetAmount.toString());
    setSelectedPreset(presetAmount);
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    const numericAmount = parseFloat(amount);

    if (isNaN(numericAmount) || numericAmount < MINIMUM_FUNDING) {
      alert(`Minimum funding amount is ₦${MINIMUM_FUNDING}`);
      return;
    }

    if (numericAmount > MAXIMUM_FUNDING) {
      alert(`Maximum funding amount is ₦${MAXIMUM_FUNDING}`);
      return;
    }

    try {
      setIsRedirecting(true);
      await fundWallet(numericAmount);
      onSuccess();
    } catch (error) {
      console.error('Funding error:', error);
    } finally {
      setIsRedirecting(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const numericAmount = parseFloat(amount) || 0;
  const fee = calculateFee(numericAmount);
  const netAmount = numericAmount - fee;
  const showFee = numericAmount >= MINIMUM_FUNDING;
  const isSubmitting = isLoading || isRedirecting;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-lg">
          <div className="absolute top-0 right-0 pt-3 pr-3 sm:pt-4 sm:pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
            >
              <X className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 sm:h-14 sm:w-14 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/30 sm:mx-0">
              <CreditCard className="h-6 w-6 sm:h-7 sm:w-7 text-white" />
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-5 sm:text-left w-full">
              <div className="flex items-center mb-1">
                <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                  Fund Your Wallet
                </h3>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">
                Securely fund your wallet via card or bank transfer
              </p>
            </div>
          </div>

          <form onSubmit={handlePayment} className="mt-5 sm:mt-6 space-y-4 sm:space-y-6">
            {/* Quick Select Presets */}
            <div>
              <label className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-2">
                Quick Select
              </label>
              <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                {presetAmounts.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handlePresetSelect(preset)}
                    className={`
                      py-2 sm:py-2.5 px-2 sm:px-3 rounded-lg text-xs sm:text-sm font-medium transition-all border
                      ${selectedPreset === preset
                        ? 'bg-primary-50 text-primary-700 border-primary-200 shadow-neomorph-inset'
                        : 'bg-white text-gray-700 border-gray-200 shadow-neomorph hover:shadow-neomorph-hover'
                      }
                    `}
                  >
                    ₦{preset.toLocaleString()}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Amount */}
            <div>
              <label htmlFor="amount" className="block text-[10px] sm:text-xs font-medium text-gray-600 mb-1">
                Or Enter Custom Amount
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 text-sm">₦</span>
                </div>
                <input
                  type="number"
                  id="amount"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSelectedPreset(null);
                  }}
                  min={MINIMUM_FUNDING}
                  max={MAXIMUM_FUNDING}
                  step="1"
                  className="block w-full pl-8 pr-3 py-2 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  placeholder="0.00"
                  disabled={isSubmitting}
                />
              </div>
              <p className="text-[9px] sm:text-xs text-gray-400 mt-1">
                Minimum: ₦{MINIMUM_FUNDING} | Maximum: ₦{MAXIMUM_FUNDING.toLocaleString()}
              </p>
            </div>

            {/* Fee Breakdown */}
            {showFee && (
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">Amount to pay</span>
                    <span className="font-medium text-gray-800">{formatAmount(numericAmount)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-600">
                      Fee ({FEE_PERCENT}%, max ₦{FEE_CAP})
                    </span>
                    <span className="font-medium text-amber-600">- {formatAmount(fee)}</span>
                  </div>
                  <div className="border-t border-gray-200/50 pt-1.5 mt-1">
                    <div className="flex justify-between items-center text-sm font-semibold">
                      <span className="text-gray-700">You will receive</span>
                      <span className="text-primary-600">{formatAmount(netAmount)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Information */}
            <div className="rounded-xl p-3 sm:p-4 bg-blue-50 shadow-neomorph-inset border border-blue-200/50">
              <div className="flex items-start">
                <div className="p-1 bg-blue-100 rounded-md shadow-neomorph-inset mr-2 sm:mr-3 flex-shrink-0">
                  <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <h4 className="text-[10px] sm:text-xs font-medium text-blue-800 mb-0.5 sm:mb-1">
                    Secure Payment via Monnify
                  </h4>
                  <p className="text-[9px] sm:text-xs text-blue-700">
                    Pay using card, bank transfer, USSD, or QR code. All transactions are secure and encrypted.
                  </p>
                </div>
              </div>
            </div>

            {/* Loading/Redirecting Info */}
            {isSubmitting && (
              <div className="rounded-xl p-3 sm:p-4 bg-yellow-50 shadow-neomorph-inset border border-yellow-200/50 animate-pulse">
                <div className="flex items-center">
                  <div className="p-1 bg-yellow-100 rounded-md shadow-neomorph-inset mr-2 sm:mr-3 flex-shrink-0">
                    <svg className="h-4 w-4 text-yellow-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-[10px] sm:text-xs font-medium text-yellow-800 mb-0.5 sm:mb-1">
                      {isRedirecting ? 'Opening Payment Page...' : 'Processing Payment...'}
                    </h4>
                    <p className="text-[9px] sm:text-xs text-yellow-700">
                      {isRedirecting 
                        ? 'Please wait while we redirect you to the secure payment page.' 
                        : 'Please do not close this window.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:justify-end sm:space-x-3 gap-2 sm:gap-0 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="order-2 sm:order-1 w-full sm:w-auto inline-flex justify-center rounded-lg px-4 py-2.5 bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !amount || numericAmount < MINIMUM_FUNDING}
                className="order-1 sm:order-2 w-full sm:w-auto inline-flex justify-center rounded-lg px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {isRedirecting ? 'Opening Payment...' : 'Processing...'}
                  </>
                ) : (
                  `Pay ${formatAmount(numericAmount)}`
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </div>
  );
};

export default FundWalletModal;